import React, {
  MouseEvent,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';

import { Button, SxProps, Typography } from '@mui/material';
import Popover, { PopoverProps } from '@mui/material/Popover';

import { classNames } from '../utils';

import styles from './StlPopoverWrapper.module.css';

interface StlPopoverWrapperProps extends PropsWithChildren {
  narrow?: boolean;
  disabled?: boolean;
  open: boolean;
  setOpen: (value: boolean) => void;
  renderLabel?: () => string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  isPlaceholderText?: boolean;
  buttonSx?: SxProps;
  onClose?: () => void;
  buttonClass?: string;
  disableRipple?: boolean;
  paperClass?: string;
}

export default function StlPopoverWrapper({
  narrow = false,
  disabled = false,
  open,
  setOpen,
  renderLabel,
  startIcon,
  endIcon = undefined,
  buttonSx = {},
  isPlaceholderText = false,
  children,
  onClose,
  buttonClass,
  disableRipple = true,
  paperClass,
  ...props
}: StlPopoverWrapperProps & PopoverProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open === false && anchorEl !== null) setAnchorEl(null);
  }, [open]);

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    onClose?.();
    setAnchorEl(null);
    setOpen(false);
  };

  const id = open ? 'selector' : undefined;
  const onlyIcon = renderLabel === undefined;
  return (
    <>
      <Button
        disableRipple={disableRipple}
        data-open={open ? 'true' : 'false'}
        disabled={disabled}
        className={classNames(
          styles.buttonSelector,
          isPlaceholderText && styles.placeholder,
          buttonClass,
        )}
        aria-describedby={id}
        variant="contained"
        onClick={handleClick}
        startIcon={startIcon}
        endIcon={endIcon}
        sx={{
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: onlyIcon && 0,
          },
          height: '24px',
          ...buttonSx,
        }}
      >
        {renderLabel && (
          <Typography variant="body2">{renderLabel()}</Typography>
        )}
      </Button>
      <Popover
        id={id}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          className: classNames(
            narrow ? styles.paperNarrow : styles.paper,
            paperClass,
          ),
        }}
        className={styles.popover}
        {...props}
      >
        {children}
      </Popover>
    </>
  );
}
